import { useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import { getFinancialSummary } from 'utils/OrderWebAPIUtils';
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function hasEntity(value) {
    return isObject(value) && 'entity' in value;
}
function isApiFinancialSummary(value) {
    if (!isObject(value))
        return false;
    // Consider it a summary if at least one known numeric field is present
    const keys = [
        'total_revenue',
        'revenue_receivables_total',
        'cost_payables_total',
        'vendor_payables_total',
        'total_cost',
        'pending_accessorial_revenue',
        'unactioned_pending_revenue',
        'unactioned_pending_cogs',
        'margin',
        'margin_pct',
    ];
    return keys.some((k) => k in value);
}
function isApiFinancialSummaryResponse(value) {
    if (!isObject(value))
        return false;
    return 'summary' in value || 'data' in value;
}
function hasFinancialSummary(value) {
    return isObject(value) && 'financialSummary' in value;
}
export function normalize(value, multiplier = 1) {
    return isNil(value) ? undefined : value * multiplier;
}
export function useOrderRtbStats(orderId) {
    return useQuery(['order-rtb-stats', orderId], async () => {
        if (!orderId)
            throw new Error('orderId is required');
        const res = await getFinancialSummary(orderId);
        const entity = hasEntity(res) ? res.entity : undefined;
        // Surface backend-declared failures
        if (isObject(entity) &&
            'success' in entity &&
            (entity === null || entity === void 0 ? void 0 : entity.success) === false) {
            const message = (entity === null || entity === void 0 ? void 0 : entity.error) ||
                'Failed to fetch order financial summary';
            throw new Error(String(message));
        }
        let s = {};
        if (hasFinancialSummary(entity)) {
            const candidate = entity
                .financialSummary;
            if (isApiFinancialSummary(candidate))
                s = candidate;
        }
        else if (isApiFinancialSummaryResponse(entity)) {
            if (isApiFinancialSummary(entity.summary))
                s = entity.summary;
            else if (isApiFinancialSummary(entity.data))
                s = entity.data;
        }
        else if (isApiFinancialSummary(entity)) {
            s = entity;
        }
        return {
            revenue: normalize(s.revenue_receivables_total),
            carrierCosts: normalize(s.cost_payables_total),
            vendorCosts: normalize(s.vendor_payables_total),
            grossProfit: normalize(s.margin),
            grossMargin: normalize(s.margin_pct, 100),
            unactionedPendingRevenue: normalize(s.unactioned_pending_revenue),
            unbilledApprovedRevenue: normalize(s.revenue_receivables_total),
            pendingAccessorialRevenue: normalize(s.pending_accessorial_revenue),
            unactionedPendingCOGS: normalize(s.unactioned_pending_cogs),
            totalCost: normalize(s.total_cost),
        };
    }, {
        enabled: Boolean(orderId),
        staleTime: 30000,
        retry: false,
    });
}
